import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Copy, Edit, Eye, FileDown, Plus, Printer, Search, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useAuthStore } from '@/store/auth.store'
import { hireEmployeeRequestService } from '@/services/hireEmployeeRequestService'
import type { HireEmployeeRequestListItem, HireEmployeeRequestListResponse } from '@/modules/hire-employee/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

export const HireEmployeeList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [statuses, setStatuses] = useState<Array<{ id?: number; lookupCodeId?: number; lookupNameEn?: string; lookupNameAr?: string }>>([])
  const [statusFilter, setStatusFilter] = useState<string>('0') // send as "status" query param
  const [rows, setRows] = useState<HireEmployeeRequestListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)

  const handleCopy = async () => {
    if (filteredRows.length === 0) {
      toast.error(t('hireEmployeePage.nothingToCopy', { defaultValue: 'Nothing to copy.' }))
      return
    }
    const header = [
      t('hireEmployeePage.colPosition'),
      t('hireEmployeePage.colSalaryFrom'),
      t('hireEmployeePage.colSalaryTo'),
      t('hireEmployeePage.colYearsOfExperience'),
      t('hireEmployeePage.colStatus'),
    ].join('\t')
    const lines = filteredRows.map((r) =>
      [
        r.position ?? '',
        r.salaryRangeFrom ?? '',
        r.salaryRangeTo ?? '',
        r.yearsOfExperience ?? '',
        String(r.status ?? ''),
      ].join('\t')
    )
    await navigator.clipboard.writeText([header, ...lines].join('\n'))
    toast.success(t('hireEmployeePage.copiedToClipboard', { defaultValue: 'Copied to clipboard.' }))
  }

  const handleExportCSV = () => {
    if (filteredRows.length === 0) {
      toast.error(t('hireEmployeePage.nothingToExport', { defaultValue: 'Nothing to export.' }))
      return
    }
    const escape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`
    const header = [
      t('hireEmployeePage.colPosition'),
      t('hireEmployeePage.colSalaryFrom'),
      t('hireEmployeePage.colSalaryTo'),
      t('hireEmployeePage.colYearsOfExperience'),
      t('hireEmployeePage.colStatus'),
    ]
      .map(escape)
      .join(',')
    const body = filteredRows
      .map((r) =>
        [
          r.position ?? '',
          r.salaryRangeFrom ?? '',
          r.salaryRangeTo ?? '',
          r.yearsOfExperience ?? '',
          String(r.status ?? ''),
        ]
          .map(escape)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `hire_employee_requests_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('hireEmployeePage.csvDownloaded', { defaultValue: 'CSV downloaded.' }))
  }

  const handlePrint = () => window.print()

  const reload = useCallback(async () => {
    if (!branchId) return
    const res: HireEmployeeRequestListResponse = await hireEmployeeRequestService.getAll(
      branchId,
      pageNumber,
      pageSize,
      statusFilter || '0'
    )
    const list = res.data || []
    setRows(list)
    setTotalRecords(res.totalRecords || 0)
  }, [branchId, pageNumber, pageSize, statusFilter])

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const list = await hireEmployeeRequestService.getStatusLookups()
        setStatuses(list || [])
      } catch {
        setStatuses([])
      }
    }
    loadStatuses()
  }, [])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        await reload()
      } catch (e: any) {
        const status = e?.response?.status as number | undefined
        const msg = (e?.response?.data?.message || e?.message || t('hireEmployeePage.fetchFailed')).toString()
        const isNoData =
          status === 404 ||
          /\b404\b|not found|no data|no records|status code 404/i.test(msg)
        setRows([])
        setTotalRecords(0)
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize, statusFilter, reload, t])

  const handleDelete = useCallback(async (id: number) => {
    const confirmed = await showDeleteConfirm(undefined, t('hireEmployeePage.deleteConfirm'))
    if (!confirmed) return
    try {
      await hireEmployeeRequestService.delete(id)
      await showSuccess(t('hireEmployeePage.deletedTitle'), t('hireEmployeePage.deletedMessage'))
      await reload()
    } catch (e: any) {
      await showError(
        t('hireEmployeePage.deleteFailedTitle'),
        e?.response?.data?.message ?? e?.message ?? t('hireEmployeePage.deleteFailedMessage')
      )
    }
  }, [reload, t])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      return (
        String(r.id ?? '').toLowerCase().includes(q) ||
        (r.position ?? '').toLowerCase().includes(q) ||
        String(r.salaryRangeFrom ?? '').toLowerCase().includes(q) ||
        String(r.salaryRangeTo ?? '').toLowerCase().includes(q) ||
        String(r.yearsOfExperience ?? '').toLowerCase().includes(q) ||
        String(r.status ?? '').toLowerCase().includes(q)
      )
    })
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)

  /** Hire request status lookup id for "Approved" (matches API / status badge). */
  const isApprovedHireStatus = (row: HireEmployeeRequestListItem) => Number(row.status ?? 0) === 435

  const statusBadge = (row: HireEmployeeRequestListItem) => {
    const statusCode = Number(row.status ?? 0)
    let text: string
    if (statusCode === 434) text = t('hireEmployeePage.statusPending')
    else if (statusCode === 435) text = t('hireEmployeePage.statusApproved')
    else if (statusCode === 436) text = t('hireEmployeePage.statusRejected')
    else text = row.status != null ? String(row.status) : '—'

    const isApproved = statusCode === 435
    const isPending = statusCode === 434
    return (
      <Badge variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('hireEmployeePage.pageTitle')} description={t('hireEmployeePage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('loanRequestPage.branchRequiredTitle', { defaultValue: 'Branch required' })}
              description={t('loanRequestPage.branchRequiredDescription', { defaultValue: 'Please select a branch first.' })}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('hireEmployeePage.pageTitle')}
        description={t('hireEmployeePage.pageDescription')}
        action={{
          label: t('hireEmployeePage.addRequest'),
          onClick: () => navigate('/self-services/hire-employee/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hireEmployeePage.labelStatus')}</label>
                <Select
                  value={statusFilter || '0'}
                  onValueChange={(v) => {
                    setStatusFilter(v)
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder={t('hireEmployeePage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('hireEmployeePage.placeholderAll')}</SelectItem>
                    {statuses
                      .filter((s) => s.id != null)
                      .map((s) => {
                        // IMPORTANT: list API expects status ids like 434/435/436 (not lookupCodeId)
                        const value = String(s.id ?? 0)
                        const label = s.lookupNameEn || s.lookupNameAr || value
                        return (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        )
                      })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('hireEmployeePage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <FileDown className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t('hireEmployeePage.copy', { defaultValue: 'Copy' })}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  {t('hireEmployeePage.print', { defaultValue: 'Print' })}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('hireEmployeePage.colPosition')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('hireEmployeePage.colSalaryFrom')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('hireEmployeePage.colSalaryTo')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('hireEmployeePage.colYearsOfExperience')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('hireEmployeePage.colStatus')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-20">
                      {t('hireEmployeePage.colView', { defaultValue: 'View' })}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-28">
                      {t('hireEmployeePage.colAction')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        {searchQuery ? t('hireEmployeePage.noMatchSearch') : t('hireEmployeePage.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id} className="border-b hover:bg-primary/5 transition-colors">
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.position ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.salaryRangeFrom ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.salaryRangeTo ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.yearsOfExperience ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{statusBadge(row)}</TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-primary hover:bg-primary/10"
                            onClick={() => navigate(`/self-services/hire-employee/${row.id}?view=1`)}
                            title={t('hireEmployeePage.viewTooltip', { defaultValue: 'View details' })}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          {(() => {
                            const isCreated = row.isEmployeeCreated === true
                            if (isCreated) return null
                            const approved = isApprovedHireStatus(row)
                            return (
                              <div className="flex justify-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-primary hover:bg-primary/10"
                                  disabled={approved}
                                  onClick={() => navigate(`/self-services/hire-employee/${row.id}`)}
                                  title={
                                    approved
                                      ? t('hireEmployeePage.editDisabledApproved', {
                                          defaultValue: 'Approved requests cannot be edited.',
                                        })
                                      : t('hireEmployeePage.editTitle')
                                  }
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                  onClick={() => handleDelete(row.id)}
                                  title={t('hireEmployeePage.deleteTitle')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )
                          })()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('loanRequestPage.pageSize', { defaultValue: 'Page size' })}</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v))
                      setPageNumber(1)
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('leaveRequestPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords, defaultValue: `Showing ${pagFrom}-${pagTo} of ${totalRecords}` })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('leaveRequestPage.previous', { defaultValue: 'Previous' })}
                  </Button>
                  <span className="text-sm">
                    {t('leaveRequestPage.pageOf', { page: pageNumber, totalPages, defaultValue: `Page ${pageNumber} of ${totalPages}` })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    {t('leaveRequestPage.next', { defaultValue: 'Next' })}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

